import React, { useEffect } from "react";
import { FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  DrawerBody,
  DrawerClose,
  DrawerHeader,
  DrawerOverlay,
  DrawerPanel,
  DrawerSection,
  DrawerSectionTitle,
  DrawerSub,
  DrawerTitle,
  StatusBadge,
} from "../Terceiros/styles";
import { formatarData, formatarMoeda } from "../../utils/calculos";
import { hasValue, toDisplayText } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { ETAPA_LABELS } from "../../utils/toc/etapas";
import { formatPct, rotaDaEtapa } from "./helpers";

const ZONA_LABELS = {
  preto: "Preto",
  vermelho: "Vermelho",
  amarelo: "Amarelo",
  verde: "Verde",
};
const ZONA_TONES = {
  preto: "danger",
  vermelho: "danger",
  amarelo: "warning",
  verde: "success",
};

function diasRestantes(status) {
  if (!status || status.pulmaoTotal == null || status.consumido == null) return null;
  const rest = Math.round(status.pulmaoTotal - status.consumido);
  return Number.isFinite(rest) ? rest : null;
}

const ObsBox = "p"; // simple p tag — we want minimum styling

export default function CriticoDrawer({ critico, precosVisiveis, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!critico) return undefined;
    function onKey(event) {
      if (event.key === "Escape") onClose?.();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [critico, onClose]);

  if (!critico) return null;

  const { amb, status, etapa } = critico;
  const tone = ZONA_TONES[status.zona] || "default";
  const dias = diasRestantes(status);
  const observacoes = amb?.[F.OBSERVACOES_GERAIS];

  function handleAbrirEtapa() {
    const rota = rotaDaEtapa(etapa);
    if (rota) navigate(rota);
    onClose?.();
  }

  return (
    <DrawerOverlay onMouseDown={onClose}>
      <DrawerPanel
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-critico-titulo"
      >
        <DrawerHeader>
          <div style={{ minWidth: 0, flex: 1 }}>
            <DrawerTitle id="home-critico-titulo">
              <span>{toDisplayText(amb[F.AMBIENTE], "Ambiente")}</span>
              <StatusBadge $tone={tone}>{ZONA_LABELS[status.zona]}</StatusBadge>
            </DrawerTitle>
            <DrawerSub>
              <span>
                Cliente <strong>{toDisplayText(amb[F.NOME_DO_CLIENTE], "—")}</strong>
              </span>
              <span>
                Pedido <strong>{toDisplayText(amb[F.NUMERO_DO_PEDIDO], "—")}</strong>
              </span>
              <span>
                Lote <strong>{toDisplayText(amb[F.NUMERO_DO_LOTE], "—")}</strong>
              </span>
            </DrawerSub>
          </div>
          <DrawerClose type="button" onClick={onClose} aria-label="Fechar">
            <FiX size={16} />
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody>
          <DrawerSection>
            <DrawerSectionTitle>Status TOC</DrawerSectionTitle>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px 16px",
                fontSize: 13,
              }}
            >
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.75 }}>
                  Etapa atual
                </div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {etapa ? ETAPA_LABELS[etapa] : "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.75 }}>
                  Pulmão consumido
                </div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {formatPct(status.pctConsumido)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.75 }}>
                  Pulmão total
                </div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {status.pulmaoTotal != null
                    ? `${Math.round(status.pulmaoTotal)} du`
                    : "—"}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.75 }}>
                  Restante
                </div>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {dias != null
                    ? dias > 0
                      ? `${dias} du`
                      : `Estourado em ${-dias} du`
                    : "—"}
                </div>
              </div>
            </div>
          </DrawerSection>

          <DrawerSection>
            <DrawerSectionTitle>Datas-chave</DrawerSectionTitle>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto",
                gap: 4,
                fontSize: 12,
              }}
            >
              <span style={{ color: "inherit", opacity: 0.7 }}>
                Aprovação do executivo
              </span>
              <span style={{ fontWeight: 600 }}>
                {formatarData(amb[F.APROVACAO_DO_EXECUTIVO]) || "—"}
              </span>
              <span style={{ opacity: 0.7 }}>Entrega prevista</span>
              <span style={{ fontWeight: 600 }}>
                {formatarData(amb[F.ENTREGA_PREVISTA]) || "—"}
              </span>
              <span style={{ opacity: 0.7 }}>Data reprogramada</span>
              <span style={{ fontWeight: 600 }}>
                {formatarData(amb[F.DATA_REPROGRAMADA]) || "—"}
              </span>
            </div>
          </DrawerSection>

          <DrawerSection>
            <DrawerSectionTitle>Detalhes</DrawerSectionTitle>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "6px 12px",
                fontSize: 13,
              }}
            >
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.7 }}>
                  Marceneiro
                </div>
                <div>{toDisplayText(amb[F.MARCENEIRO_RESPONSAVEL], "—")}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.7 }}>
                  Vendedor
                </div>
                <div>{toDisplayText(amb[F.VENDEDOR], "—")}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, textTransform: "uppercase", opacity: 0.7 }}>
                  Valor do ambiente
                </div>
                <div>
                  {precosVisiveis ? formatarMoeda(amb[F.VALOR_DO_AMBIENTE]) : "••••••"}
                </div>
              </div>
            </div>
          </DrawerSection>

          {hasValue(observacoes) && String(observacoes).trim() && (
            <DrawerSection>
              <DrawerSectionTitle>Observações</DrawerSectionTitle>
              <ObsBox
                style={{
                  margin: 0,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: "1px dashed",
                  borderColor: "currentColor",
                  fontSize: 12,
                  lineHeight: 1.5,
                  opacity: 0.85,
                  whiteSpace: "pre-wrap",
                }}
              >
                {String(observacoes).trim()}
              </ObsBox>
            </DrawerSection>
          )}

          {etapa && (
            <button
              type="button"
              onClick={handleAbrirEtapa}
              style={{
                alignSelf: "flex-start",
                padding: "8px 14px",
                borderRadius: 999,
                border: "1px solid currentColor",
                background: "transparent",
                color: "inherit",
                font: "inherit",
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Abrir página de {ETAPA_LABELS[etapa]}
            </button>
          )}
        </DrawerBody>
      </DrawerPanel>
    </DrawerOverlay>
  );
}
